/**
 * FinalBrokerPreviewModal — Stage B final confirmation step.
 * Talks to the new order-intent flow:
 *   1. POST /api/broker/orders/preview  → generates a 60 s snapshot
 *   2. POST /api/broker/orders/submit    → creates immutable OrderIntent + Alpaca submission
 *
 * This is opened AFTER the proposal is approved / prepared. It refreshes
 * broker state, re-runs the check-list, and blocks submission on any
 * material change.
 */
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, ShieldCheck, RefreshCcw, AlertTriangle } from "lucide-react";

function fmt$(n, d = 2) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: d, minimumFractionDigits: d });
}

function timeRemaining(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
}

export default function FinalBrokerPreviewModal({
  open,
  onClose,
  proposalId,
  planId,
  portfolioKey = "SWING",
  orderType = "market",
  orderPurpose = "entry",
  onSubmitted,
}) {
  const [preview, setPreview] = useState(null);
  const [orderTypeState, setOrderType] = useState(orderType);
  const [purposeState, setPurpose] = useState(orderPurpose);
  const [bracketEntryType, setBracketEntryType] = useState("market");
  const [limitPrice, setLimitPrice] = useState("");
  const [stopPrice, setStopPrice] = useState("");
  const [takeProfitPrice, setTakeProfitPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(60);

  const regen = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const body = {
        proposal_id: proposalId, plan_id: planId,
        portfolio_key: portfolioKey,
        order_type: orderTypeState,
        order_purpose: purposeState,
        time_in_force: "day",
      };
      if ((orderTypeState === "limit" || orderTypeState === "stop_limit") && limitPrice)
        body.limit_price = Number(limitPrice);
      if ((orderTypeState === "stop" || orderTypeState === "stop_limit") && stopPrice)
        body.stop_price = Number(stopPrice);
      if (orderTypeState === "bracket") {
        body.bracket_entry_type = bracketEntryType;
        if (bracketEntryType === "limit" && limitPrice) body.limit_price = Number(limitPrice);
        if (takeProfitPrice) body.take_profit_price = Number(takeProfitPrice);
        if (stopPrice) body.stop_price = Number(stopPrice);
      }
      const { data } = await api.post("/broker/orders/preview", body);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && proposalId && planId) regen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposalId, planId, orderTypeState, purposeState, bracketEntryType]);

  // Tick countdown
  useEffect(() => {
    if (!preview?.expires_at) return;
    setRemaining(timeRemaining(preview.expires_at));
    const t = setInterval(() => setRemaining(timeRemaining(preview.expires_at)), 1000);
    return () => clearInterval(t);
  }, [preview?.expires_at]);

  const expired = remaining <= 0;
  const canSubmit = useMemo(
    () => Boolean(preview?.all_checks_ok) && !expired && !submitting,
    [preview, expired, submitting],
  );

  const submit = async () => {
    if (!preview || !canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/broker/orders/submit", {
        preview_id: preview.id, confirm: true,
      });
      toast.success(`Order ${data.status} · broker id ${(data.broker_order_id || "").slice(0, 10)}`);
      onSubmitted?.(data);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const CheckRow = ({ c }) => (
    <div className="flex items-baseline gap-2 text-[11px]" data-testid={`preview-check-${c.name}`}>
      {c.ok
        ? <CheckCircle2 size={12} className="pnl-up flex-none"/>
        : <XCircle size={12} className="pnl-down flex-none"/>}
      <span className="mono uppercase text-[10px] flex-none w-40">{c.name.replace(/_/g, " ")}</span>
      <span className="text-muted-foreground truncate">{c.detail || (c.ok ? "OK" : "FAIL")}</span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl !rounded-none" data-testid="final-broker-preview-modal">
        <DialogHeader>
          <DialogTitle className="mono uppercase tracking-widest text-xs flex items-baseline gap-2">
            <ShieldCheck size={14}/> Final Alpaca Paper Preview
            <span className="chip !text-[9px] border-signal-yellow text-signal-yellow ml-auto">PAPER · NO REAL MONEY</span>
          </DialogTitle>
        </DialogHeader>

        {/* Order type selector — Stage B2: market + limit + stop + stop_limit */}
        <div className="space-y-1" data-testid="preview-order-type-row">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="label-cap">Order type</span>
            {[
              { id: "market", label: "market" },
              { id: "limit", label: "limit" },
              { id: "stop", label: "stop" },
              { id: "stop_limit", label: "stop-limit" },
              { id: "bracket", label: "bracket" },
            ].map(t => (
              <button key={t.id} onClick={() => setOrderType(t.id)}
                className={`chip cursor-pointer ${orderTypeState === t.id ? "border-signal-blue text-signal-blue" : "border-border/50 text-muted-foreground"}`}
                data-testid={`preview-type-${t.id}`}>{t.label}</button>
            ))}
            <button className="btn-outline h-6 !px-2 text-[10px] ml-auto" onClick={regen} disabled={loading}
              data-testid="preview-refresh-btn">
              <RefreshCcw size={10} className={loading ? "animate-spin" : ""}/> Refresh
            </button>
          </div>

          {(orderTypeState === "stop" || orderTypeState === "stop_limit") && (
            <div className="flex gap-2 items-center flex-wrap pt-1" data-testid="preview-purpose-row">
              <span className="label-cap">Purpose</span>
              {[
                { id: "entry", label: "entry (breakout)" },
                { id: "protective_exit", label: "protective exit" },
                { id: "standalone_exit", label: "standalone exit" },
              ].map(p => (
                <button key={p.id} onClick={() => setPurpose(p.id)}
                  className={`chip cursor-pointer ${purposeState === p.id ? "border-signal-yellow text-signal-yellow" : "border-border/50 text-muted-foreground"}`}
                  data-testid={`preview-purpose-${p.id}`}>{p.label}</button>
              ))}
            </div>
          )}

          {orderTypeState === "bracket" && (
            <div className="flex gap-2 items-center flex-wrap pt-1" data-testid="preview-bracket-row">
              <span className="label-cap">Bracket entry</span>
              {[
                { id: "market", label: "market" },
                { id: "limit", label: "limit" },
              ].map(t => (
                <button key={t.id} onClick={() => setBracketEntryType(t.id)}
                  className={`chip cursor-pointer ${bracketEntryType === t.id ? "border-signal-yellow text-signal-yellow" : "border-border/50 text-muted-foreground"}`}
                  data-testid={`preview-bracket-entry-${t.id}`}>{t.label}</button>
              ))}
              <label className="text-[10px] mono uppercase text-muted-foreground flex items-center gap-1 ml-2">
                Take-Profit
                <input value={takeProfitPrice} onChange={(e) => setTakeProfitPrice(e.target.value)}
                  placeholder="target" type="number" step="0.01"
                  className="mono text-xs w-24 h-6 px-2 border border-border/50 bg-transparent"
                  data-testid="preview-take-profit-input"/>
              </label>
              <label className="text-[10px] mono uppercase text-muted-foreground flex items-center gap-1">
                Stop-Loss
                <input value={stopPrice} onChange={(e) => setStopPrice(e.target.value)}
                  placeholder="stop" type="number" step="0.01"
                  className="mono text-xs w-24 h-6 px-2 border border-border/50 bg-transparent"
                  data-testid="preview-bracket-stop-input"/>
              </label>
              {bracketEntryType === "limit" && (
                <label className="text-[10px] mono uppercase text-muted-foreground flex items-center gap-1">
                  Entry
                  <input value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)}
                    placeholder="limit" type="number" step="0.01"
                    className="mono text-xs w-24 h-6 px-2 border border-border/50 bg-transparent"
                    data-testid="preview-bracket-limit-input"/>
                </label>
              )}
            </div>
          )}

          <div className="flex gap-2 items-center flex-wrap pt-1">
            {(orderTypeState === "stop" || orderTypeState === "stop_limit") && (
              <label className="text-[10px] mono uppercase text-muted-foreground flex items-center gap-1">
                Stop
                <input value={stopPrice} onChange={(e) => setStopPrice(e.target.value)}
                  placeholder="trigger" type="number" step="0.01"
                  className="mono text-xs w-24 h-6 px-2 border border-border/50 bg-transparent"
                  data-testid="preview-stop-input"/>
              </label>
            )}
            {(orderTypeState === "limit" || orderTypeState === "stop_limit") && (
              <label className="text-[10px] mono uppercase text-muted-foreground flex items-center gap-1">
                Limit
                <input value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)}
                  placeholder="limit" type="number" step="0.01"
                  className="mono text-xs w-24 h-6 px-2 border border-border/50 bg-transparent"
                  data-testid="preview-limit-input"/>
              </label>
            )}
          </div>
        </div>

        {loading && <p className="text-[11px] text-muted-foreground italic">Loading preview…</p>}

        {preview && (
          <div className="space-y-3">
            {(preview.warnings || []).length > 0 && (
              <div className="border border-signal-yellow/50 p-2 space-y-1" data-testid="preview-warnings">
                {(preview.warnings || []).map((w, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-[11px] text-signal-yellow" data-testid={`preview-warning-${i}`}>
                    <AlertTriangle size={11} className="flex-none"/><span>{w}</span>
                  </div>
                ))}
              </div>
            )}

            {(preview.tick_normalizations || []).length > 0 && (
              <div className="border border-border/40 p-2 text-[11px] space-y-1" data-testid="preview-tick-notes">
                <div className="label-cap">Broker-compatible tick adjustment</div>
                {preview.tick_normalizations.map((t, i) => (
                  <div key={i} className="mono flex justify-between" data-testid={`preview-tick-${t.field}`}>
                    <span>{t.field}</span>
                    <span>${fmt$(t.requested)} → ${fmt$(t.normalized)} <span className="text-muted-foreground">tick ${t.tick}</span></span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border/60" data-testid="preview-summary">
              <div className="grid-cell !p-2"><div className="label-cap">Symbol</div><div className="mono text-sm">{preview.symbol}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Side / Qty</div><div className="mono text-sm">{preview.side} · {preview.requested_qty}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Order</div><div className="mono text-sm">{preview.order_type} · {preview.time_in_force}</div></div>
              {preview.stop_price != null && (
                <div className="grid-cell !p-2"><div className="label-cap">Trigger (stop)</div><div className="mono text-sm text-signal-yellow">${fmt$(preview.stop_price)}</div></div>
              )}
              {preview.limit_price != null && (
                <div className="grid-cell !p-2"><div className="label-cap">Limit</div><div className="mono text-sm">${fmt$(preview.limit_price)}</div></div>
              )}
              {preview.order_type === "bracket" && preview.take_profit_price != null && (
                <div className="grid-cell !p-2" data-testid="preview-bracket-tp"><div className="label-cap">Take Profit</div><div className="mono text-sm pnl-up">${fmt$(preview.take_profit_price)}</div></div>
              )}
              {preview.order_type === "bracket" && preview.bracket_stop_price != null && (
                <div className="grid-cell !p-2" data-testid="preview-bracket-sl"><div className="label-cap">Stop Loss</div><div className="mono text-sm pnl-down">${fmt$(preview.bracket_stop_price)}</div></div>
              )}
              <div className="grid-cell !p-2"><div className="label-cap">Ref / Entry</div><div className="mono text-sm">${fmt$(preview.expected_entry_price)}</div></div>
              {preview.distance_to_trigger_pct != null && (
                <div className="grid-cell !p-2"><div className="label-cap">Distance to Trigger</div><div className="mono text-sm">{preview.distance_to_trigger_pct}%</div></div>
              )}
              <div className="grid-cell !p-2"><div className="label-cap">Notional</div><div className="mono text-sm">${fmt$(preview.estimated_notional)}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Max Loss</div><div className="mono text-sm pnl-down">${fmt$(preview.max_planned_loss)}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Reward</div><div className="mono text-sm pnl-up">${fmt$(preview.potential_profit)}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">R:R</div><div className="mono text-sm">{preview.reward_to_risk ?? "—"}:1</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Portfolio</div><div className="mono text-sm">{preview.portfolio_key}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Buying Power</div><div className="mono text-sm">${fmt$(preview.buying_power)}</div></div>
              <div className="grid-cell !p-2"><div className="label-cap">Market</div><div className="mono text-sm">{preview.market_is_open ? "OPEN" : "CLOSED"}</div></div>
            </div>

            <div className="space-y-1" data-testid="preview-check-list">
              <div className="label-cap flex items-baseline justify-between">
                <span>Pre-flight checks</span>
                <span className={`chip ${preview.all_checks_ok ? "pnl-up border-current" : "pnl-down border-current"}`}>
                  {preview.all_checks_ok ? "ALL PASS" : "BLOCKED"}
                </span>
              </div>
              {preview.checks.map(c => <CheckRow key={c.name} c={c}/>)}
            </div>

            {!preview.all_checks_ok && (
              <div className="border border-signal-red/60 p-2 text-[11px] flex items-baseline gap-2" data-testid="preview-blocked-banner">
                <AlertTriangle size={12} className="pnl-down flex-none"/>
                <span>One or more checks failed — submission disabled. Adjust the plan or refresh once conditions change.</span>
              </div>
            )}

            <div className="flex items-baseline justify-between border-t border-border/40 pt-2">
              <div className={`text-[11px] mono flex items-baseline gap-2 ${expired ? "pnl-down" : "text-muted-foreground"}`}>
                <Clock size={11}/>
                {expired ? "PREVIEW EXPIRED — refresh required" : `Preview valid for ${remaining}s`}
              </div>
              <div className="text-[10px] mono text-muted-foreground">Preview {preview.id.slice(0, 8)} · Env {preview.environment}</div>
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <button className="btn-ghost" onClick={onClose} data-testid="preview-cancel-btn">Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={!canSubmit}
            data-testid="preview-confirm-submit-btn">
            {submitting ? "Submitting…" : "Confirm & Submit Paper Order"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
